import * as openpgp from "openpgp";
import { IDecryptedKeys } from "./decryptKeys";
import { generateSingleBinary } from "./GenerateSingleBinary";
import { ICompanyKeyGrantPayload } from "dto/ICompanyKey";

/**
 * Ładunek PIERWSZEJ generacji klucza firmy — wspólny dla dwóch ścieżek, które muszą go
 * zbudować IDENTYCZNIE:
 *   * `hooks/useCreateTeam`      — zakładanie firmy (POST /api/workspace, pole `CompanyKey`),
 *   * `components/CompanyKeyBootstrapGate` — firma JUŻ istnieje, ale nie ma klucza
 *                                  (POST /api/workspace/key).
 *
 * Kształt jest kontraktem z BE (`Data/Workspace/CreateRequest.CompanyKey` oraz
 * `Data/WorkspaceKeys/CreateCompanyKeyRequest`) — nazwy CO DO LITERY, bo ASP.NET po cichu
 * pomija pola, których nie zna.
 */
export interface ICreateCompanyKeyPayload {
  /** base64 binarnego packetlistu klucza PUBLICZNEGO firmy (I3 — nie ASCII-armor). */
  PublicKey: string;
  /**
   * 16 znaków hex identyfikatora PODKLUCZA SZYFRUJĄCEGO (nie klucza głównego) — bez niego
   * `ResolveWorkspaceKeyFromBlob` nie dopasuje generacji do pakietu PKESK szyfrogramu.
   */
  KeyIdHex: string;
  /**
   * Granty wydawane razem z generacją. MUSI być wśród nich grant dla ZAKŁADAJĄCEGO —
   * generacja bez ani jednego grantu to klucz firmy, którego nikt nigdy nie odszyfruje,
   * a serwer nie ma jak tego naprawić, bo klucza prywatnego nie zna (I4).
   */
  Grants: ICompanyKeyGrantPayload[];
}

/**
 * Identyfikator podklucza szyfrującego w postaci hex.
 *
 * `getKeyId()` oddaje w runtime obiekt `Keyid` (typy @types/openpgp deklarują `string` —
 * stąd rzutowanie), a openpgp v4 z krzywą secp256k1 tworzy klucz główny ORAZ osobny podklucz
 * szyfrujący. PKESK wskazuje na TEN DRUGI, więc odczyt identyfikatora klucza głównego dałby
 * wartość, po której BE nigdy nie dopasuje generacji.
 */
export const encryptionKeyIdHex = async (
  publicKey: openpgp.key.Key,
): Promise<string> => {
  const encryptionKey = await publicKey.getEncryptionKey();

  if (!encryptionKey) {
    throw new Error("Wygenerowany klucz firmy nie ma podklucza szyfrującego");
  }

  return (
    encryptionKey.getKeyId() as unknown as { toHex: () => string }
  ).toHex();
};

/**
 * Generuje parę kluczy FIRMY i składa ładunek z jednym grantem — dla samego zakładającego.
 *
 * NIENARUSZALNE (I4): klucz prywatny firmy NIE OPUSZCZA przeglądarki odszyfrowany.
 * `generateSingleBinary` z kluczem pośrednim oddaje od razu `PrivateKey` w postaci
 * `PGP(intermediate.pub zakładającego){ company.priv }`, czyli gotowy grant — nigdzie po
 * drodze nie powstaje jawny blob, który dałoby się wysłać przez pomyłkę.
 */
export const buildCompanyKeyPayload = async (
  userId: string,
  myKeys: IDecryptedKeys | null | undefined,
): Promise<ICreateCompanyKeyPayload> => {
  if (!myKeys?.intermidatePrivateKey) {
    throw new Error(
      "Brak klucza pośredniego — nie ma czym owinąć klucza firmy dla zakładającego",
    );
  }

  const companyKeys = await generateSingleBinary(
    undefined,
    myKeys.intermidatePrivateKey,
  );

  return {
    PublicKey: companyKeys.PublicKey,
    KeyIdHex: await encryptionKeyIdHex(companyKeys.cleanPublicKey.keys[0]),
    Grants: [{ UserId: userId, PrivateKey: companyKeys.PrivateKey }],
  };
};
